import { useEffect, useMemo } from "react";
import {
  CFW_NON_PERSISTENT_STATE,
  CFW_PROMISE_FETCHER_STATE,
} from "@genericComponents/CFW.js";
import { HasProperty } from "@genericComponents/Common.js";
import { PostDataWorker } from "@genericComponents/SSRFW.js";
import {
  GetState,
  GetStateField,
  RemoveState,
  SetDefaultData,
  SetState,
  UpdateStateField,
} from "@genericComponents/StoreToolkit.js";
const GenerateStateName =
  await import("@asyncImports/GenerateStateName.js").then(
    (mod) => mod.GenerateStateName,
  );
const MatchingFieldsOnly =
  await import("@asyncImports/MatchingFieldsOnly.js").then(
    (mod) => mod.MatchingFieldsOnly,
  );
const CleanUrl = await import("@asyncImports/CleanUrl.js").then(
  (mod) => mod.CleanUrl,
);
export const useGenerateStateListEntry = (
  state,
  action,
  params,
  concatName = "",
  updateParams = null,
  postEvent = null,
  preEvent = async () => {
    return true;
  },
  isClientSideRetrieveOnly = false,
) => {
  const finalPreEvent = async () => {
    if (preEvent) {
      return preEvent();
    }
    return true;
  };
  if (concatName) {
    concatName = CleanUrl(concatName);
  } else {
    concatName = "";
  }
  const defaultDataStateName = GenerateStateName(
    state,
    CFW_PROMISE_FETCHER_STATE,
    concatName,
  );
  const currentViewState = GenerateStateName(
    state,
    CFW_NON_PERSISTENT_STATE,
    "view",
    concatName,
  );
  const filterStateName = GenerateStateName(state, "filter", concatName);
  const entryStateName = GenerateStateName(state, "entry", concatName);
  const fetchStatusName = GenerateStateName(state, "fetch-status", concatName);
  const listStateName = GenerateStateName(state, concatName);
  const fetchData = async (pageParam = 0) => {
    SetState(fetchStatusName, true);
    finalPreEvent().then(async (isValid) => {
      if (isValid) {
        const filterParam = MatchingFieldsOnly(
          GetState(filterStateName, { ...params }),
          { ...params },
        );
        if (
          HasProperty(filterParam, "PageIndex") &&
          filterParam.PageIndex !== -1
        ) {
          filterParam.PageIndex = pageParam;
        }
        if (filterParam.PageIndex > 0) {
          filterParam.PreviousId = pageParam - 1;
        }
        UpdateStateField(filterStateName, "PageIndex", pageParam);
        return await PostDataWorker(
          action,
          filterParam,
          null,
          null,
          false,
          postEvent,
        ).then((res) => {
          const currentResult =
            filterParam.PageIndex > 0 ? GetState(listStateName) : [];
          SetState(
            listStateName,
            [...currentResult, ...(res || [])],
            null,
            true,
          );
          if ((filterParam.PageIndex ?? 0) <= 0 && !isClientSideRetrieveOnly) {
            SetDefaultData(defaultDataStateName, listStateName);
          }
          RemoveState(defaultDataStateName);
          if (res?.length) {
            RemoveState(fetchStatusName);
          } else {
            SetState(fetchStatusName, 2);
          }
          return res;
        });
      }
      return null;
    });
  };
  const PostData = (param) => {
    return PostDataWorker(
      action,
      param.param,
      null,
      false,
      false,
      param.postEvent,
    );
  };
  const defaultData = () => {
    const currentData = GetState(listStateName);
    if (
      !currentData &&
      (!isClientSideRetrieveOnly ||
        (isClientSideRetrieveOnly && typeof window !== "undefined"))
    ) {
      if (!GetState(defaultDataStateName)) {
        SetState(
          defaultDataStateName,
          fetchData().then((results) => {
            return results;
          }),
        );
      }
      return GetState(defaultDataStateName, GetState(listStateName));
    }
    return currentData;
  };
  const setDefaultValues = () => {
    if (!GetState(filterStateName)) {
      SetState(filterStateName, { ...params });
    }
    if (!GetState(entryStateName)) {
      SetState(entryStateName, { ...updateParams });
    }
  };
  if (typeof window === "undefined") {
    setDefaultValues();
  }
  useEffect(() => {
    setDefaultValues();
  }, [setDefaultValues]);
  const Search = async (refetch = true) => {
    const filterParam = MatchingFieldsOnly(
      GetState(filterStateName, { ...params }),
      { ...params },
    );
    if (HasProperty(filterParam, "PageIndex") && filterParam.PageIndex !== -1) {
      filterParam.PageIndex = 0;
    }
    SetState(filterStateName, filterParam);
    if (refetch) {
      fetchData(0);
    }
  };
  return useMemo(
    () => ({
      currentViewState,
      entry: {
        state: entryStateName,
        param: { ...updateParams },
        UpdateData: (currentParam) => {
          PostData(currentParam).then(() => {
            Search(true);
          });
        },
      },
      error: { state: GenerateStateName(state, "entry", concatName, "error") },
      filter: { state: filterStateName, param: { ...params } },
      list: {
        Search,
        defaultData,
        fetchStatusName,
        state: listStateName,
        results: {
          fetchNextPage: () => {
            const nextPage = GetStateField(filterStateName, "PageIndex", 0) + 1;
            fetchData(nextPage);
          },
          hasNextPage: (rowCount) => {
            return !(rowCount % 50);
          },
        },
      },
      refresherState: GenerateStateName(state, concatName, "refresher"),
    }),
    [
      GenerateStateName,
      defaultData,
      state,
      entryStateName,
      listStateName,
      concatName,
      params,
      updateParams,
      Search,
      fetchStatusName,
      state,
      action,
      postEvent,
      finalPreEvent,
    ],
  );
};
